import { useEffect } from 'react';

import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import { helper } from '@myrealtrip/frontend-weblog';
import { IcoReset } from '@myrealtrip/web-icons';
import { BottomSheet, ButtonGeneral, Text, UnstyledButton } from '@myrealtrip/web-ui';

import Show from '~components/Show';
import { WeblogEventName, WeblogPageCategory, WeblogScreenName } from '~constants/weblog';
import useTravelerOption from '~hooks/pointToPointTicket/useTravelerOption';

import TravelerAgeSelectSheet from './SearchType/Traveler/TravelerAgeSelectSheet';
import TravelerCountStepper from './SearchType/Traveler/TravelerCountStepper';
import TravelerAgeList from './SearchType/Traveler/TravelerAgeList';
import TravelerNotificationList from './SearchType/Traveler/TravelerNotificationList';

const { sendClickLog, sendLayerViewLog } = helper;

interface Props {
  isOpen: boolean;
  currentTravelers?: number[];
  onClose: () => void;
  onApply: (travelers: number[], closeAfterApply?: boolean) => void;
}

const TravelerConditionSheet = ({ isOpen, currentTravelers, onApply, onClose }: Props) => {
  const theme = useTheme();

  const {
    travelers,
    setTravelers,
    selectedTraveler,
    setSelectedTraveler,
    viewSelectedTravelerAge,
    setViewSelectedTravelerAge,
    handleApplyTravelers,
    handleChangeTravelerAge,
    handleDecreaseCount,
    handleIncreaseCount,
    handleIncreaseTraveler,
    handleResetCount,
  } = useTravelerOption({ currentTravelers, onApply });

  const onApplyTravelers = () => {
    sendClickLog({
      pageCategory: WeblogPageCategory.EUROPE_TRAIN_TICKET_MAIN,
      screenName: WeblogScreenName.EUROPE_TRAIN_TICKET_MAIN,
      eventName: WeblogEventName.APPLY_PAX,
      itemKind: 'button',
      itemName: '인원 적용',
      pax: travelers.length,
      age: travelers.map((age) => age.toString()).join(','),
    });

    handleApplyTravelers({ updatedTravelers: travelers });
  };

  useEffect(() => {
    if (!isOpen) return;

    sendLayerViewLog({
      pageCategory: WeblogPageCategory.EUROPE_TRAIN_TICKET_MAIN,
      screenName: WeblogScreenName.EUROPE_TRAIN_TICKET_MAIN,
      eventName: WeblogEventName.PAX,
      itemName: '탑승 인원',
    });
  }, [isOpen]);

  return (
    <BottomSheet
      isOpen={isOpen}
      detent="full"
      headerProps={{
        titleAlign: 'left',
        children: (
          <Text typography="headlineBold20" color={theme.colors.gray.s1000} as="p">
            탑승 인원
          </Text>
        ),
        closable: true,
        hasGrapple: true,
      }}
      backdropProps={{
        onClick: onClose,
      }}
      preventScrollProps={{ ignoreIosScroll: true }}
      contentProps={{
        className: css`
          padding-bottom: 64px;
        `,
        disableDrag: true,
      }}
      onClose={onClose}
      onXClick={onClose}
    >
      <TravelerCountContainer>
        <CountStepperSection>
          <Text typography="paragraphBold15" color={theme.colors.gray.s1000}>
            인원 선택
          </Text>
          <TravelerCountStepper
            currentValue={travelers.length}
            onIncrease={handleIncreaseCount}
            onDecrease={handleDecreaseCount}
            min={1}
          />
        </CountStepperSection>
        <Show when={travelers.length > 0}>
          <TravelerAgeList
            travelers={travelers}
            viewSelectedTravelerAge={viewSelectedTravelerAge}
            exposureAgeSelectView={() => setViewSelectedTravelerAge(true)}
            updateTravelers={setTravelers}
            updateSelectedTraveler={setSelectedTraveler}
          />
        </Show>
        <TravelerNotificationList />
        <Show when={viewSelectedTravelerAge}>
          <TravelerAgeSelectSheet
            isOpen={viewSelectedTravelerAge}
            registeredTravelerCount={travelers.length}
            selectedTraveler={selectedTraveler}
            onApply={(age: number) => handleIncreaseTraveler(age)}
            onModify={(index: number, age: number) => handleChangeTravelerAge(index, age)}
            onClose={() => setViewSelectedTravelerAge(false)}
          />
        </Show>
        <CTAContainer>
          <ResetButtonText onClick={handleResetCount}>
            <IcoReset size={20} />
            <Text typography="paragraphBold14">초기화</Text>
          </ResetButtonText>
          <ButtonGeneral
            size="medium"
            shape="rectangle"
            disabled={travelers.length === 0}
            onClick={onApplyTravelers}
          >
            <Text typography="headlineNormal15">인원 적용</Text>
          </ButtonGeneral>
        </CTAContainer>
      </TravelerCountContainer>
    </BottomSheet>
  );
};

const TravelerCountContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  height: 100%;
`;

const CountStepperSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
`;

const CTAContainer = styled.footer`
  position: fixed;
  bottom: 0;
  width: 100%;
  display: flex;
  z-index: 1;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  background-color: ${({ theme: { colors } }) => colors.white};
  padding: 8px 20px calc(constant(safe-area-inset-bottom) + 8px);
  padding: 8px 20px calc(env(safe-area-inset-bottom) + 8px);
  box-shadow: ${({ theme: { shadows } }) => shadows.E100V};
`;

const ResetButtonText = styled(UnstyledButton)`
  display: flex;
  align-items: center;
  gap: 4px;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
`;

export default TravelerConditionSheet;
